<template>
  <div class="star-rating" :class="{ 'compact': compact, 'small': small, 'read-only': readOnly }">
    <div class="stars-container">
      <div
        v-for="star in 5"
        :key="star"
        class="star-wrapper"
        @mouseenter="!readOnly && handleHover(star)"
        @mouseleave="!readOnly && handleLeave()"
        @click="!readOnly && handleClick(star)"
      >
        <svg
          class="star"
          :class="[getStarClass(star), { 'orange': orangeHover && hoverValue >= star }]"
          viewBox="0 0 24 24"
        >
          <defs v-if="shouldShowHalfStar(star)">
            <linearGradient :id="`gradient-${componentId}-${star}`">
              <stop offset="50%" :stop-color="getStarColor(star, true)" />
              <stop offset="50%" :stop-color="getStarColor(star, false)" />
            </linearGradient>
          </defs>
          <path
            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            :fill="shouldShowHalfStar(star) ? `url(#gradient-${componentId}-${star})` : getStarColor(star, true)"
          />
        </svg>
      </div>
    </div>

    <span v-if="showCount && ratingCount > 0" class="rating-count">
      ({{ ratingCount }})
    </span>
  </div>
</template>

<script>
export default {
  name: 'StarRating',

  props: {
    modelValue: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0 && value <= 5
    },
    userRating: {
      type: Number,
      default: 0
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    showCount: {
      type: Boolean,
      default: false
    },
    ratingCount: {
      type: Number,
      default: 0
    },
    orangeHover: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'rate'],

  data () {
    return {
      hoverValue: 0,
      flashingStar: null,
      componentId: `star-${Math.random().toString(36).substr(2, 9)}`
    }
  },

  methods: {
    handleHover (star) {
      if (!this.readOnly) {
        this.hoverValue = star
      }
    },

    handleLeave () {
      if (!this.readOnly) {
        this.hoverValue = 0
      }
    },

    handleClick (star) {
      if (!this.readOnly) {
        this.flashingStar = star
        this.$emit('update:modelValue', star)
        this.$emit('rate', star)

        // Clear flash animation after duration
        setTimeout(() => {
          this.flashingStar = null
        }, 600)
      }
    },

    getStarClass (star) {
      if (this.flashingStar === star) {
        return 'star-flash'
      }

      const displayValue = this.modelValue // Use modelValue directly, not getDisplayValue

      if (this.hoverValue > 0) {
        return this.hoverValue >= star ? 'star-hover' : 'star-empty'
      }

      // For read-only (track average), always show gold
      if (this.readOnly) {
        if (displayValue >= star) {
          return 'star-full' // Gold stars for average
        }
        if (this.shouldShowHalfStar(star)) {
          return 'star-half'
        }
        return 'star-empty'
      }

      // For interactive (user rating), show blue when filled
      if (displayValue >= star) {
        return 'star-user' // Blue stars for user rating
      }

      if (this.shouldShowHalfStar(star)) {
        return 'star-half'
      }

      return 'star-empty'
    },

    getDisplayValue () {
      // Always return modelValue (don't override with userRating)
      return this.modelValue
    },

    shouldShowHalfStar (star) {
      const displayValue = this.getDisplayValue()
      const decimal = displayValue - Math.floor(displayValue)
      return star === Math.ceil(displayValue) && decimal >= 0.3 && decimal < 0.8 && this.hoverValue === 0
    },

    getStarColor (star, filled) {
      // Hover state (orange or yellow depending on orangeHover prop)
      if (this.hoverValue > 0 && this.hoverValue >= star) {
        return this.orangeHover ? '#ffa500' : '#ffed4e'
      }

      const displayValue = this.modelValue

      // Read-only mode: Always show gold (track average)
      if (this.readOnly) {
        if (displayValue >= star) {
          return '#ffd700' // Gold for average rating
        }
        if (this.shouldShowHalfStar(star)) {
          return filled ? '#ffd700' : '#555'
        }
        return '#555' // Empty
      }

      // Interactive mode: Show blue (user rating)
      if (displayValue >= star) {
        return '#66b2ff' // Blue for user rating
      }

      if (this.shouldShowHalfStar(star)) {
        return filled ? '#66b2ff' : '#555'
      }

      return '#555' // Empty color
    }
  }
}
</script>

<style scoped>
.star-rating {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.star-rating.compact {
  gap: 0.3rem;
}

.stars-container {
  display: flex;
  gap: 0.2rem;
}

.star-rating.compact .stars-container {
  gap: 0.1rem;
}

.star-wrapper {
  display: inline-flex;
  cursor: pointer;
}

.star-rating.read-only .star-wrapper {
  cursor: default;
}

.star {
  width: 20px;
  height: 20px;
  transition: transform 0.2s ease;
}

.star-rating.compact .star {
  width: 16px;
  height: 16px;
}

.star-rating.small .star {
  width: 18px;
  height: 18px;
}

.star-wrapper:not(.read-only):hover .star {
  transform: scale(1.1);
}

.star-full {
  fill: #ffd700;
}

.star-empty {
  fill: #555;
}

.star-hover {
  fill: #ffed4e;
}

.star-hover.orange {
  fill: #ffa500;
}

.star-user {
  fill: #66b2ff;
}

.star-half {
  /* Color handled by gradient */
}

@keyframes flash {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  25% {
    transform: scale(1.3);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.5);
    opacity: 1;
  }
  75% {
    transform: scale(1.2);
    opacity: 0.9;
  }
}

.star-flash {
  animation: flash 0.6s ease-out;
}

.rating-count {
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.star-rating.compact .rating-count {
  font-size: 0.8em;
}
</style>
